import React, {Component} from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import _ from 'lodash';
import { fetchProjects, fetchProjectsCreatedTime } from 'ducks/projects';
import { HEADER_HEIGHT } from 'style/dimensions';
import Row from 'components/UIKit/Flex/Row';
import Column from 'components/UIKit/Flex/Column';
import Card from 'components/UIKit/Card';
import FilterCard from 'components/UIKit/Card/FilterCard';
import { SpinnerOverlay } from 'components/UIKit/Spinner';
import CenteredLayout from 'components/layouts/CenteredLayout';
import ProjectsTable from 'components/Tables/ProjectsTable';
import DocumentationIcon from 'components/icons/DocumentationIcon';
import { ExternalLink } from 'components/Links';
import UserDropdownContainer from 'containers/UserDropdownContainer';
import Modal from 'components/UIKit/Modal/Modal';
import GDCAppsDropdown from 'components/GDCAppsDropdown';
import POReports from 'components/POReports';
import Pagination from 'components/Pagination';
import { parsePaginationParams, hasPaginationChanged } from 'utils/location';
import { GDCLogo } from 'components/Theme';
import InitialDisclaimer from 'components/Disclaimers/InitialDisclaimer';
import withRouting from 'utils/withRouting';

class Home extends Component {
  static propTypes = {
    projects: PropTypes.object,
    reports: PropTypes.object,
    projectRoles: PropTypes.object,
    dispatch: PropTypes.func,
    router: PropTypes.any,
  };

   // $FlowIgnore
  state = {
    modalIsOpen: !localStorage.acceptedDisclaimer,
    dispatchedProjects: false,
  };

  componentDidMount() {
    let {
      dispatch,
      history,
      projectRoles,
      router
    } = this.props;
    let { sort, order, first, offset } = parsePaginationParams(router);
    dispatch(fetchProjects({ ids: _.keys(projectRoles), first, offset }));
    this.setState({ dispatchedProjects: true });
  }

  componentDidUpdate(prevProps) {
    let {
      dispatch,
      history,
      projectRoles,
    } = this.props;
    let nextRouter = this.props.router;
    let router = prevProps.router;
    let { sort, order, first, offset } = parsePaginationParams(nextRouter);
    if (hasPaginationChanged(router, nextRouter) || !_.isEqual(prevProps.projectRoles, projectRoles)) {
      dispatch(fetchProjects({ ids: _.keys(projectRoles), first, offset }));
    }
  }

  projectsWithReports(projectRoles) {
    return _.keys(_.pick(projectRoles, roles => _.includes(roles, 'read_report')));
  }

  acceptDisclaimer = () => {
    this.setState({ modalIsOpen: false });
    localStorage.acceptedDisclaimer = true;
  };

  render(): React.Element {
    let { projects, router, projectRoles } = this.props;
    let { sort, order, first } = parsePaginationParams(router);
    let { modalIsOpen, dispatchedProjects } = this.state;

    let appName = `GDC Data Submission Portal`;
    let isFetching = projects.isFetching || projects.isFetchingCreated || !dispatchedProjects;

    let hasReadReportRole = _.flatten(Object.values(projectRoles || {})).includes('read_report');

    return (
      <Column style={{flex: 1}}>
        <Modal isOpen={modalIsOpen}>
          <InitialDisclaimer handleClick={this.acceptDisclaimer} />
        </Modal>
        <Row style={{height: `${HEADER_HEIGHT}rem`, backgroundColor: '#fafafa'}}>
          <Row
            style={{
              order: 3,
              flex: 1,
              alignItems: 'center',
              justifyContent: 'flex-end'
            }}
          >
            <UserDropdownContainer
              style={{
                border: '1px solid #efefef',
                borderRight: 'none',
              }}
            />
            <GDCAppsDropdown
              style={{
                border: '1px solid #efefef',
                borderRight: 'none'
              }}
            />
          </Row>
        </Row>
        <CenteredLayout>
          <Row><GDCLogo style={{height: '7rem'}}/></Row>
          <Row>
            <Card title={`welcome to the gdc data submission portal`} style={{ flex: 8 }}>
              {`The ${appName} allows researchers to submit and release clinical,
              biospecimen, and experimental data for studies registered in dbGaP
              into GDC. Select a project from the project list to submit and release
              data as well as view previously submitted data and transactions.`}
            </Card>

            <Card HeaderIcon={DocumentationIcon} title="documentation" style={{ flex: 4 }}>
              <ExternalLink
                href="https://docs.gdc.cancer.gov/Data_Submission_Portal/Users_Guide/Checklist/">
                User's Guide
              </ExternalLink>
              <span>Tutorial (Coming soon)</span>
              <ExternalLink
                href="https://docs.gdc.cancer.gov/Data_Submission_Portal/Users_Guide/Data_Submission_Overview/">
                Submission Workflow
              </ExternalLink>
            </Card>
          </Row>
          {hasReadReportRole &&
            <Column>
              <POReports projects={isFetching ? [] : this.projectsWithReports(projectRoles)} />
            </Column>
          }
          <Column data-testid="table-projects">
            <FilterCard
              title={`projects ${isFetching ? '' : `(${ projects.count })`}`}
              style={{ flexShrink: 0 }}
              label="Filter Projects"
            >
              <SpinnerOverlay isLoading={isFetching} >
                <ProjectsTable
                  isFetching={isFetching}
                  router={router}
                  sort={sort}
                  order={order}
                  projectRoles={projectRoles}
                >
                  {isFetching ? [] : projects.items}
                </ProjectsTable>
                {
                  projects.count > first ?
                    <Pagination
                      total={projects.count || projects.items.length}
                      router={router}
                      size={first}
                    />
                    : null
                }
              </SpinnerOverlay>
            </FilterCard>
          </Column>
        </CenteredLayout>
      </Column>
    );
  }
}

export default connect(
  (state) => ({
    projects: state.projects,
    reports: state.reports,
    projectRoles: state?.auth?.user?.projects?.gdc_ids,
  })
)(withRouting(Home));
